import { z } from "zod";

// Payment Method Enum
export const paymentMethodEnum = z.enum([
  "bank_transfer",
  "credit_card",
  "debit_card",
  "cash",
  "check",
  "paypal",
  "other",
]);

export type PaymentMethod = z.infer<typeof paymentMethodEnum>;

// Payment Provider Type Enum
export const paymentProviderTypeEnum = z.enum(["stripe", "paypal"]);
export type PaymentProviderType = z.infer<typeof paymentProviderTypeEnum>;

// Base Payment Schema
export const basePaymentSchema = z.object({
  invoiceId: z.string().optional().nullable(),
  creditNoteId: z.string().optional().nullable(),
  expenseId: z.string().optional().nullable(),
  amount: z.coerce.number().positive("Amount must be positive"),
  paymentDate: z.date().optional().nullable(),
  paymentMethod: paymentMethodEnum.optional().nullable(),
  reference: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),

  // BANK ACCOUNT DETAILS
  bankAccountId: z.string().optional().nullable(),
  bankAccountName: z.string().optional().nullable(),
  bankAccountCountry: z.string().optional().nullable(),
  bankAccountCurrency: z.string().optional().nullable(),
  bankAccountIban: z.string().optional().nullable(),

  // External Payment Provider Details (Stripe/PayPal)
  externalPaymentId: z.string().optional().nullable(),
  externalProvider: paymentProviderTypeEnum.optional().nullable(),
  externalStatus: z.string().optional().nullable(),
  payerEmail: z.string().email().optional().nullable(),
  payerName: z.string().optional().nullable(),
});
export const createPaymentSchema = basePaymentSchema.extend({});

export type CreatePaymentSchema = z.infer<typeof createPaymentSchema>;

export const paymentDocumentType = z.enum(["invoice", "credit_note", "expense"]);
export type PaymentDocumentType = z.infer<typeof paymentDocumentType>;

// Payment Schema (for responses)
export const paymentSchema = basePaymentSchema.extend({
  id: z.string(),
  paymentNumber: z.string(),
  documentType: paymentDocumentType,

  userId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type PaymentSchema = z.infer<typeof paymentSchema>;

// Update Payment Schema
export const updatePaymentSchema = createPaymentSchema.extend({
  id: z.string(),
});

// Transaction type for filtering (Credit = money in, Debit = money out)
export const transactionTypeEnum = z.enum(["credit", "debit"]);
export type TransactionType = z.infer<typeof transactionTypeEnum>;

export const getPaymentsParamsSchema = z.object({
  // Entity filters
  projectId: z.string().optional(),
  invoiceId: z.string().optional(),
  expenseId: z.string().optional(),
  creditNoteId: z.string().optional(),
  // Text search filters
  paymentNumber: z.string().optional(),
  documentNumber: z.string().optional(),
  reference: z.string().optional(),
  bankAccountName: z.string().optional(),
  // Option filters
  documentType: z
    .union([paymentDocumentType, z.array(paymentDocumentType)])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  transactionType: z
    .union([transactionTypeEnum, z.array(transactionTypeEnum)])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  paymentMethod: z
    .union([paymentMethodEnum, z.array(paymentMethodEnum)])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  // Date range filters
  paymentDateFrom: z.coerce.date().optional(),
  paymentDateTo: z.coerce.date().optional(),
});

export type UpdatePaymentSchema = z.infer<typeof updatePaymentSchema>;
export type ZodCreatePaymentSchema = z.infer<typeof createPaymentSchema>;
export type ZodGetPaymentParams = z.infer<typeof getPaymentsParamsSchema>;

export const createPaymentSchemaDefaultValues: ZodCreatePaymentSchema = {
  invoiceId: undefined,
  creditNoteId: undefined,
  amount: 0,
  paymentDate: new Date(),
  paymentMethod: "bank_transfer",
  reference: "",
  notes: "",
};
